import {
  ComponentProps,
  createEffect,
  createMemo,
  createSignal,
  JSX,
  onCleanup,
  untrack,
} from "solid-js";
import c from "class-c";
import { eventHandler } from "solid-u";
import {
  createInputMask,
  createMaskPattern,
  InputMask,
} from "@solid-primitives/input-mask";

import createDelayed from "@repo/signals/createDelayed";
import createId from "@repo/signals/createId";
import UObject from "@repo/utils/UObject";
import UEventTarget from "@repo/utils-client/UEventTarget";
import createPropsProvider from "@repo/utils-solid/createPropsProvider";

import ContainerButton from "../buttons/ContainerButton";

import Input from "./Input";

import styles from "./TextInput.module.scss";

type MaskEvent = (InputEvent | ClipboardEvent) & {
  currentTarget: HTMLInputElement;
  target: Element;
};

declare namespace TextInput {
  type Props = UObject.Override<
    ComponentProps<"input">,
    {
      ref?: ComponentProps<"label">["ref"];
      label: string;
      format?(str: string): string;
      error?: Input.Error;
      action?: JSX.Element;
      pattern?: string | ((value?: string) => string);
      mask?: InputMask;
      onBeforeMask?(e: MaskEvent): void;
    }
  >;
}

function TextInput(_props: TextInput.Props) {
  const {
    class: className,
    label,
    onInput,
    onFocus,
    onBlur,
    onKeyDown,
    format = (s) => s,
    placeholder,
    error,
    ref,
    action,
    id = createId(),
    pattern,
    mask: _mask,
    onBeforeMask,
    ...props
  } = TextInput.PropsProvider.useMerge(_props) satisfies D;

  const mask = createMemo(() => _mask);

  const [open, setOpen] = createSignal(!!props.value);
  const [focused, setFocused] = createSignal(false);

  const showPlaceholder = createDelayed(open, 50);

  createEffect(() => {
    setOpen(!!props.value || untrack(focused));
  });

  return (
    <label
      ref={ref}
      class={c`
        ${styles["text-input"]}
        ${className}
      `}
    >
      <main
        class={c(styles)`
          ${{ open, focused, error }}
        `}
      >
        <header>{label}</header>
        {/* Input mask label */}
        <label for={id} />
        <input
          ref={(input) => {
            if (mask())
              onCleanup(
                UEventTarget.wrap(input).on(
                  ["input", "change", "paste"],
                  (_e) => {
                    const e = _e as MaskEvent;
                    onBeforeMask?.(e);
                    (pattern
                      ? createMaskPattern(
                          createInputMask(mask()!),
                          typeof pattern === "function"
                            ? pattern
                            : () => pattern || ""
                        )
                      : createInputMask(mask()!))(e);
                  }
                ).off
              );
          }}
          id={id}
          type="text"
          placeholder={
            showPlaceholder()
              ? placeholder ||
                (typeof pattern === "string"
                  ? pattern
                  : // No need to pass value to pattern function, pattern is only visible when value is empty
                    pattern?.())
              : undefined
          }
          onInput={(e) => {
            const { value } = e.currentTarget;

            if (!focused()) setOpen(!!value);

            e.currentTarget.value = format(value);

            eventHandler(onInput)(e);
          }}
          onFocus={(e) => {
            setOpen(true);
            setFocused(true);

            eventHandler(onFocus)(e);
          }}
          onBlur={(e) => {
            if (e.currentTarget) setOpen(!!e.currentTarget.value);
            setFocused(false);

            eventHandler(onBlur)(e);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const action = props.enterkeyhint;
              if (action === "go") {
                e.currentTarget.form?.dispatchEvent(new Event("submit"));
                e.currentTarget.blur();
              }
            }

            eventHandler(onKeyDown)(e);
          }}
          {...props}
        />
        {action}
      </main>
      <Input.Error>{error}</Input.Error>
    </label>
  );
}

TextInput.PropsProvider = createPropsProvider<TextInput.Props>("TextInput");

declare namespace TextInput.Action {
  export type Props = ContainerButton.Props & {};
}

TextInput.Action = Action;
function Action({
  class: className,
  children,
  ...props
}: D<TextInput.Action.Props>) {
  return (
    <ContainerButton
      tabIndex={-1}
      class={c`${styles["text-input-action"]} ${className}`}
      {...props}
    >
      {children}
    </ContainerButton>
  );
}

declare namespace TextInput.Action.Visibility {
  export type Props = TextInput.Action.Props & {
    onClick(): void;
    hidden: boolean;
  };
}

Action.Visibility = ({
  onClick,
  hidden,
}: D<TextInput.Action.Visibility.Props>) => {
  return (
    <TextInput.Action onClick={onClick} class="icon:medium color:moderate">
      {hidden ? "" : ""}
    </TextInput.Action>
  );
};

export default TextInput;
